import { TierIIReportKind } from "encamp-prisma/generated/client/client";
import { AutomationRunner } from "encampy";
import { AlternateId, PortalCredential } from "generated-graphql/graphql";
import { useMemo } from "react";
import { getEncampyLoginAutomation } from "util/encampy/portalLogin";
import {
  EncampyAutomationLauncher,
  EncampyAutomationLauncherProps,
  EncampyLaunchData,
} from "./AutomationLauncher";

export interface EncampyPortalLoginLauncherProps
  extends Omit<EncampyAutomationLauncherProps, "launchData"> {
  portalCredential?: PortalCredential | null;
  facilityAlternateIds?: AlternateId[] | null;
  reportingYear?: number;
  reportKind?: TierIIReportKind;
}

export function EncampyPortalLoginLauncher({
  portalCredential,
  facilityAlternateIds,
  reportingYear,
  reportKind,
  disabled = false,
  disabledReason,
  ...rest
}: EncampyPortalLoginLauncherProps) {
  const launchData = useMemo((): EncampyLaunchData | null => {
    if (!portalCredential?.url) {
      return null;
    }

    const singlePhaseAutomation = portalCredential
      ? getEncampyLoginAutomation(
          portalCredential,
          facilityAlternateIds,
          reportingYear,
          reportKind
        )
      : null;

    if (!singlePhaseAutomation) {
      return null;
    }

    return {
      url: portalCredential.url,
      automation: async (runner: AutomationRunner) => {
        await runner.run(singlePhaseAutomation);
      },
    };
  }, [portalCredential, facilityAlternateIds, reportingYear, reportKind]);

  // Synchronous validation - no async loading/error states needed
  const computedDisabledReason = useMemo((): string | undefined => {
    if (disabled && disabledReason) {
      return disabledReason;
    }
    if (!launchData) {
      return "Portal credential URL is required";
    }
    if (disabled) {
      return "Portal login is disabled";
    }
    return undefined;
  }, [launchData, disabled, disabledReason]);

  // Provide placeholder for disabled state
  const effectiveLaunchData: EncampyLaunchData = launchData ?? {
    url: portalCredential?.url || "",
    automation: async () => {
      // No-op automation for disabled state
    },
  };

  return (
    <EncampyAutomationLauncher
      launchData={effectiveLaunchData}
      disabled={disabled || !launchData}
      disabledReason={computedDisabledReason}
      {...rest}
    />
  );
}
